import { useState, useRef } from 'react';
import { FileSpreadsheet, Download, Upload, CheckCircle, XCircle, AlertCircle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export default function BulkDataManager() {
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [conflictAction, setConflictAction] = useState('ask');
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const downloadExport = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/admin/export-all`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cricorama_data_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully!');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setDownloading(false);
    }
  };

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API}/admin/import-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cricorama_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls'];
    const extension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(extension)) {
      toast.error('Please upload an Excel file (.xlsx)');
      return;
    }
    
    setFile(selectedFile);
    setResults(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/admin/import-all?conflict_action=${conflictAction}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setResults(response.data);
      
      const totalCreated = 
        (response.data.scorers?.created || 0) +
        (response.data.leagues?.created || 0) +
        (response.data.teams?.created || 0) +
        (response.data.players?.created || 0) +
        (response.data.matches?.created || 0);
      
      if (totalCreated > 0) {
        toast.success(`Successfully imported ${totalCreated} records!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setResults(null);
  };

  const getTotalErrors = () => {
    if (!results) return 0;
    return (
      (results.scorers?.errors?.length || 0) +
      (results.leagues?.errors?.length || 0) +
      (results.teams?.errors?.length || 0) +
      (results.players?.errors?.length || 0) +
      (results.matches?.errors?.length || 0)
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-teal-600" />
          Bulk Data Manager
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Import and export leagues, teams, players, and matches
        </p>
      </div>

      {/* Action Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Card */}
        <Card className="border-2 border-dashed hover:border-teal-300 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Download className="w-5 h-5 text-teal-600" />
              Export All Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Download all your leagues, teams, players, and matches as an Excel file.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Leagues with details</li>
              <li>• Teams with league mapping</li>
              <li>• Players with team mapping</li>
              <li>• Matches with schedules</li>
              <li>• Scorer accounts</li>
            </ul>
            <Button 
              onClick={downloadExport}
              disabled={downloading}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="export-data-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export to Excel
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Import Card */}
        <Card className="border-2 border-dashed hover:border-blue-300 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Upload className="w-5 h-5 text-blue-600" />
              Import Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Bulk import leagues, teams, players, matches, and scorers from Excel.
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>• Auto-creates scorer accounts</li>
              <li>• Links existing players (no duplicates)</li>
              <li>• Handles conflicts your way</li>
              <li>• Detailed import report</li>
            </ul>
            <Dialog open={importOpen} onOpenChange={(open) => {
              setImportOpen(open);
              if (!open) resetImport();
            }}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="import-data-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import from Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                    Import Data
                  </DialogTitle>
                  <DialogDescription>
                    Upload an Excel file to bulk import data
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 pt-4">
                  {/* Template Download */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="text-sm text-blue-700">
                      <strong>Need a template?</strong> Download to see the required format
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={downloadTemplate}
                      disabled={downloadingTemplate}
                      className="text-blue-600"
                    >
                      {downloadingTemplate ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-1" />
                          Template
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Conflict Action */}
                  <div className="space-y-2">
                    <Label>When records already exist:</Label>
                    <Select value={conflictAction} onValueChange={setConflictAction}>
                      <SelectTrigger data-testid="conflict-action-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ask">Ask me (skip and show conflicts)</SelectItem>
                        <SelectItem value="skip">Skip existing records</SelectItem>
                        <SelectItem value="update">Update existing records</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* File Upload Zone */}
                  {!results && (
                    <div
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                        dragOver 
                          ? 'border-blue-500 bg-blue-50' 
                          : file 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDrop={handleDrop}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="import-file-input"
                      />
                      
                      {file ? (
                        <div className="space-y-2">
                          <CheckCircle className="w-10 h-10 mx-auto text-green-500" />
                          <p className="font-medium text-green-700">{file.name}</p>
                          <p className="text-sm text-green-600">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                            className="text-gray-500"
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="w-10 h-10 mx-auto text-gray-400" />
                          <p className="font-medium text-gray-700">
                            Drop your Excel file here or click to browse
                          </p>
                          <p className="text-sm text-gray-500">
                            Supports .xlsx files
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Upload Button */}
                  {file && !results && (
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={handleUpload}
                      disabled={uploading}
                      data-testid="import-submit-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Start Import
                        </>
                      )}
                    </Button>
                  )}

                  {/* Results */}
                  {results && (
                    <div className="space-y-4">
                      {/* Summary Grid */}
                      <div className="grid grid-cols-5 gap-2">
                        {['scorers', 'leagues', 'teams', 'players', 'matches'].map(type => (
                          <Card key={type} className="text-center">
                            <CardContent className="p-3">
                              <div className="text-xl font-bold text-teal-600">
                                {(results[type]?.created || 0) + (results[type]?.linked || 0) + (results[type]?.updated || 0)}
                              </div>
                              <div className="text-xs text-gray-500 capitalize">{type}</div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>

                      {/* Detailed Results */}
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 font-semibold text-sm border-b">
                          Import Details
                        </div>
                        <div className="divide-y max-h-60 overflow-y-auto">
                          {['scorers', 'leagues', 'teams', 'players', 'matches'].map(type => (
                            <div key={type} className="px-4 py-3 flex items-center justify-between">
                              <span className="capitalize font-medium">{type}</span>
                              <div className="flex items-center gap-3 text-sm">
                                {results[type]?.created > 0 && (
                                  <span className="text-green-600">
                                    +{results[type].created} new
                                  </span>
                                )}
                                {results[type]?.linked > 0 && (
                                  <span className="text-blue-600">
                                    {results[type].linked} linked
                                  </span>
                                )}
                                {results[type]?.updated > 0 && (
                                  <span className="text-amber-600">
                                    {results[type].updated} updated
                                  </span>
                                )}
                                {results[type]?.skipped > 0 && (
                                  <span className="text-gray-500">
                                    {results[type].skipped} skipped
                                  </span>
                                )}
                                {results[type]?.errors?.length > 0 && (
                                  <span className="text-red-600">
                                    {results[type].errors.length} errors
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Errors */}
                      {getTotalErrors() > 0 && (
                        <div className="border border-red-200 rounded-lg overflow-hidden">
                          <div className="bg-red-50 px-4 py-2 font-semibold text-sm text-red-700 border-b border-red-200">
                            Errors ({getTotalErrors()})
                          </div>
                          <div className="max-h-40 overflow-y-auto divide-y divide-red-100">
                            {['scorers', 'leagues', 'teams', 'players', 'matches'].flatMap(type => 
                              (results[type]?.errors || []).map((err, idx) => (
                                <div key={`${type}-${idx}`} className="px-4 py-2 text-sm flex items-start gap-2">
                                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                  <span>
                                    <span className="font-medium capitalize">{type}</span> Row {err.row}: {err.error}
                                  </span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {/* Conflicts */}
                      {results.conflicts?.length > 0 && (
                        <div className="border border-amber-200 rounded-lg overflow-hidden">
                          <div className="bg-amber-50 px-4 py-2 font-semibold text-sm text-amber-700 border-b border-amber-200">
                            Conflicts ({results.conflicts.length}) - Records skipped
                          </div>
                          <div className="max-h-40 overflow-y-auto divide-y divide-amber-100">
                            {results.conflicts.map((conflict, idx) => (
                              <div key={idx} className="px-4 py-2 text-sm flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <span>
                                  <span className="font-medium capitalize">{conflict.type}</span>: "{conflict.name}" already exists
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={resetImport}
                        >
                          Import More
                        </Button>
                        <Button 
                          className="flex-1 bg-teal-600 hover:bg-teal-700"
                          onClick={() => setImportOpen(false)}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-teal-50 to-blue-50 border-none">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 mb-3">📋 Import Order & Tips</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p className="font-medium mb-2">Import processes in this order:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Scorers (created first for match assignment)</li>
                <li>Leagues</li>
                <li>Teams (linked to leagues)</li>
                <li>Players (linked to teams, no duplicates by phone)</li>
                <li>Matches (linked to leagues, teams, scorers)</li>
              </ol>
            </div>
            <div>
              <p className="font-medium mb-2">Tips:</p>
              <ul className="space-y-1">
                <li>• Download template first to see format</li>
                <li>• Player phone numbers must be unique</li>
                <li>• Team/League names must match exactly</li>
                <li>• Date format: YYYY-MM-DD HH:MM</li>
                <li>• Export first to backup existing data</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
